
import { Mixins, Component, Provide, Prop } from "vue-property-decorator";
import { mapState } from "vuex";

// types
import { IInvoice, IInvoiceProduct } from "@/models/invoices";
import { IState } from "@/store";
import { IStatus } from "@/models/statuses";

// enums
import { DataModules } from "@/store/modules/data";

// mixins
import RequestPromises from "@/mixins/requestPromises";
import {
  IInvoiceCreditRefundProvider,
  IInvoiceCreditRefundProviderMethods
} from "@/models/providers/invoiceCreditRefund";
import { IPayment } from "@/models/payment";
import { RefundSources } from "@/data/enums/payments";
import { IProvider } from "@/models/providers";
import { CreditRefundOptionTypes, CreditRefundOption } from "@/models/invoices";
import { CreateElement } from "vue";

@Component({
  computed: {
    ...mapState({
      invoiceStatuses: (state: IState) => state.constants.statuses.invoice,
      providerInvoiceScope(state: IState, getters): string {
        return getters[`data/${DataModules.INVOICES}/scope`](this.invoiceId);
      },
      invoice(state: IState, getters): IInvoice {
        return getters["data/single"]({
          id: this.invoiceId,
          scope: this.providerInvoiceScope,
          storeModule: DataModules.INVOICES
        });
      },
      isDue(state: IState, getters): string {
        return getters[`data/${DataModules.INVOICES}/isDue`](this.invoice);
      },
      isOverdue(state: IState, getters): string {
        return getters[`data/${DataModules.INVOICES}/isOverdue`](this.invoice);
      },
      isPartiallyPaid(state: IState, getters): string {
        return getters[`data/${DataModules.INVOICES}/isPartiallyPaid`](
          this.invoice
        );
      },
      isPaid(state: IState, getters): string {
        return getters[`data/${DataModules.INVOICES}/isPaid`](this.invoice);
      },
      isRefunded(state: IState, getters): string {
        return getters[`data/${DataModules.INVOICES}/isRefunded`](this.invoice);
      },
      isCreditNote(state: IState, getters): string {
        return getters[`data/${DataModules.INVOICES}/isCreditNote`](
          this.invoice
        );
      },
      isCancelled(state: IState, getters): string {
        return getters[`data/${DataModules.INVOICES}/isCancelled`](
          this.invoice
        );
      },
      hasCreditRefundOptions(state: IState, getters): string {
        return getters[`data/${DataModules.INVOICES}/hasCreditRefundOptions`](
          this.invoice
        );
      }
    })
  }
})
export default class InvoiceCreditRefundProvider extends Mixins(RequestPromises)
  implements IProvider {
  @Prop({ required: true })
  public invoiceId!: IInvoice["id"];

  @Prop({ default: true })
  public handleLoading!: boolean;

  @Prop({ default: () => ({}) })
  public loaderAttrs!: Record<string, any>;

  @Prop({ default: true })
  public handleRequestError!: boolean;

  public invoice!: IInvoice;

  public invoiceStatuses!: IStatus[];
  public providerInvoiceScope!: string;

  public isDue!: boolean;
  public isPaid!: boolean;
  public isRefunded!: boolean;
  public isCancelled!: boolean;
  public isCreditNote!: boolean;
  public isOverdue!: boolean;
  public isPartiallyPaid!: boolean;
  public hasCreditRefundOptions!: boolean;

  public isLoading = true;
  public isReloading = false;
  public hasRequestError = false;
  public isProcessing = false;

  public async created() {
    await this.loadData({ ignoreStored: true });
  }

  @Provide("invoiceCreditRefundProviderMethods")
  public get providerMethods(): IInvoiceCreditRefundProviderMethods {
    return {
      creditProducts: this.creditProducts,
      creditAmounts: this.creditAmounts,
      reloadData: this.reloadData,
      refundTransactions: this.refundTransactions
    };
  }

  @Provide("invoiceCreditRefundProvider")
  public providerData(): IInvoiceCreditRefundProvider {
    return {
      creditRefundOptionsFiltered: this.creditRefundOptionsFiltered,
      hasCreditRefundOptions: this.hasCreditRefundOptions,
      hasNonRefundedPayments: this.hasNonRefundedPayments,
      hasPaymentMethod: this.hasPaymentMethod,
      hasRequestError: this.hasRequestError,
      invoice: this.invoice,
      invoiceId: this.invoiceId,
      isCancelled: this.isCancelled,
      isClientDeleted: this.isClientDeleted,
      isCreditNote: this.isCreditNote,
      isLoading: this.isLoading,
      isOverdue: this.isOverdue,
      isPaid: this.isPaid,
      isPartiallyPaid: this.isPartiallyPaid,
      isProcessing: this.isProcessing,
      isRefunded: this.isRefunded,
      isReloading: this.isReloading,
      status: this.status
    };
  }

  public creditRefundOption: CreditRefundOption[] = [
    {
      id: CreditRefundOptionTypes.CREDIT_ENTIRE_INVOICE,
      title: "_sentence.issue_credit_note_for_the_whole_invoice"
    },
    {
      id: CreditRefundOptionTypes.CREDIT_SELECT_ITEMS,
      title: "_sentence.issue_credit_note_for_select_invoice_items"
    },
    {
      id: CreditRefundOptionTypes.REFUND_INVOICE_PAYMENTS,
      title: "_sentence.issue_full_or_partial_refund"
    }
  ];

  public get creditRefundOptionsFiltered() {
    if (this.isLoading || this.isReloading) return [];
    const creditRefundOption: CreditRefundOption[] = [];
    if (this.hasNonRefundedPayments) {
      creditRefundOption.push(
        this.$_.find(this.creditRefundOption, [
          "id",
          CreditRefundOptionTypes.REFUND_INVOICE_PAYMENTS
        ]) as CreditRefundOption
      );
    } else {
      creditRefundOption.push(
        this.$_.find(this.creditRefundOption, [
          "id",
          CreditRefundOptionTypes.CREDIT_ENTIRE_INVOICE
        ]) as CreditRefundOption
      );
    }

    if (this.invoice.allow_product_credit) {
      creditRefundOption.push(
        this.$_.find(this.creditRefundOption, [
          "id",
          CreditRefundOptionTypes.CREDIT_SELECT_ITEMS
        ]) as CreditRefundOption
      );
    }

    return creditRefundOption;
  }

  public get isClientDeleted() {
    return !this.$_.get(this.invoice, "client", false);
  }

  public get status(): IStatus {
    const status = this.$_.find(
      this.invoiceStatuses,
      s => s.id === this.$_.get(this.invoice, "status_id")
    );
    return { code: "", ...status } as IStatus;
  }

  public get hasNonRefundedPayments() {
    return (
      !this.$_.isEmpty(this.$_.get(this.invoice, "payments")) &&
      this.invoice.paid_amount_converted > 0
    );
  }

  public get hasPaymentMethod() {
    return !!(
      this.$_.get(this.invoice, "payment_details_id") ||
      this.$_.get(this.invoice, "gateway_id")
    );
  }

  public beforeDestroy() {
    this.$store.dispatch("data/binSingle", {
      storeModule: DataModules.INVOICES,
      scope: this.providerInvoiceScope,
      vm: this
    });
  }

  public async loadData({ ignoreStored } = { ignoreStored: false }) {
    try {
      await this.getInvoiceData(ignoreStored);
      this.hasRequestError = false;
    } catch (error) {
      this.$store.dispatch("api/handleError", error);
      this.hasRequestError = true;
    } finally {
      this.isLoading = false;
      this.isReloading = false;
    }
  }

  public reloadData() {
    this.isReloading = true;
    return this.loadData({ ignoreStored: true });
  }

  public getInvoiceData(ignoreStored) {
    return this.$store.dispatch(`data/${DataModules.INVOICES}/get`, {
      id: this.invoiceId,
      scope: this.providerInvoiceScope,
      params: {
        with: [
          "client",
          "currency",
          "payments.gateway",
          "payments.payment_details",
          "payments.payment_type",
          "products.options.product",
          "products.product",
          "promotions",
          "status",
          "taxes",
          "taxes.tax_tag_data"
        ].join(",")
      },
      ignoreStored,
      vm: this
    });
  }

  public creditProducts(
    products: { invoice_product_id: IInvoiceProduct["id"]; amount?: number }[],
    refundSource: RefundSources
  ): Promise<IInvoice> {
    return this.$store.dispatch(`data/${DataModules.INVOICES}/creditProducts`, {
      invoiceId: this.invoiceId,
      data: {
        products,
        refund_source: refundSource
      }
    });
  }

  public creditAmounts(
    transactions: {
      invoice_payment_id: IPayment["id"];
      amount?: number;
    }[],
    refundSource: RefundSources,
    fullCredit = false
  ): Promise<IInvoice> {
    return this.$store.dispatch(`data/${DataModules.INVOICES}/creditAmount`, {
      invoiceId: this.invoiceId,
      data: {
        transactions,
        refund_source: refundSource,
        full_credit: fullCredit
      }
    });
  }

  public refundTransactions(
    transactions: {
      invoice_payment_id: IPayment["id"];
      amount?: number;
    }[]
  ): Promise<IPayment[]> {
    return this.$store.dispatch(
      `data/${DataModules.PAYMENTS}/refundTransactions`,
      {
        data: {
          transactions,
          refund_source: RefundSources.ORIGINAL_SOURCE
        }
      }
    );
  }

  //  Render

  public render(h: CreateElement) {
    // Handle loading
    if (this.handleLoading && this.isLoading)
      return h("is-loading", { props: { if: true }, attrs: this.loaderAttrs });
    // Handle request error
    if (this.handleRequestError && this.hasRequestError)
      return h("request-error", {
        props: { isLoading: this.isReloading },
        on: { click: () => this.reloadData() }
      });
    return (
      this.$scopedSlots?.default &&
      this.$scopedSlots.default({
        $icrProviderData: this.providerData(),
        $icrProviderMethods: this.providerMethods
      })
    );
  }
}
